import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Paper, IconButton, Tooltip } from '@mui/material';
import { Panel } from './Panel';
import { Selection } from './orgNavTypes';
import { useOrgNavState } from './useOrgNavState';
import { OrgTable } from './Tables/OrgTable';
import { SystemTable } from './Tables/SystemTable/SystemTable';
import { EmployeesTable } from './Tables/EmployeesTable';
import type { SystemFilter } from '@/types/systems';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { SystemAssigned } from '@consts/systems';
import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import { FiltersDrawer } from './FiltersDrawer';
// Chevron icons for toggling the filter drawer
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FilterListIcon from '@mui/icons-material/FilterList';

/**
 * Table with tabs for each systems, locations, resellers, companies
 *
 * Org entities only use main org filters, for orgs since all of the data already exists locally, we should use a fake pagination of 30 elements per page
 * Systems use main org filters + extra filters, they do not exist locally so actual pagination is required, should use same pagination # as Org
 *
 * Due to this difference in behavior, we should split this into 2 seperate components:
 * - OrgTable
 * - SystemTable
 *
 * All behavior which describes the datatable + data loading should be encapsulated here.
 * The main org filters are shared, so should be passed down as props to both components.
 */

export type FilterInput = {
    companies: { [key: string]: boolean };
    resellers: { [key: string]: boolean };
    locations: { [key: string]: boolean };
};

export const OrgNav = () => {
    const [selection, setSelection] = useOrgNavState();

    // Current user's role utilities
    const [searchParams, setSearchParams] = useSearchParams();

    const tabParam = (searchParams.get('tab') as Selection) || Selection.Company;

    const [selectedTab, setSelectedTab] = useState<Selection>(tabParam);

    // Sync tab state to URL whenever it changes
    useEffect(() => {
        if (searchParams.get('tab') !== selectedTab) {
            searchParams.set('tab', selectedTab);
            setSearchParams(searchParams);
        }
    }, [selectedTab]);

    const [orgFilters, setOrgFilters] = useState<FilterInput>({
        companies: {},
        resellers: {},
        locations: {},
    });

    const [systemFilters, setSystemFilters] = useState<SystemFilter>({
        model: {},
        status: {},
    });

    const [assigned, setAssigned] = useState(SystemAssigned.Assigned);

    const [deleteEmployee, setDeleteEmployee] = useState<any>(null);

    const [searchTerm, setSearchTerm] = useLocalStorage<string>({
        key: 'systems_table_search_term',
        value: '',
    });

    /**
     * Determines whether the filters drawer is visible.
     * Default is `true` so the drawer starts expanded per UX requirements.
     */
    const [filtersOpen, setFiltersOpen] = useState<boolean>(true);

    // Force a window resize event whenever the drawer is toggled so that
    // components such as MUI DataGrid can recalculate their dimensions.
    useEffect(() => {
        // Dispatching on the next tick avoids layout thrashing within React's commit phase.
        const timeout = setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
        return () => clearTimeout(timeout);
    }, [filtersOpen]);

    /**
     * Pixel width of the filters drawer.
     * Must match the `width` prop inside FiltersDrawer for consistent alignment.
     */
    const FILTER_DRAWER_WIDTH_PX = 218;
    const TOGGLE_BTN_WIDTH = 42;
    const TOGGLE_BTN_HEIGHT = 42;

    /**
     * Top offset so the toggle aligns with the drawer header (drawer starts at 160px margin-top)
     */
    const DRAWER_HEADER_TOP_PX = 160; // 8px just below the Breadcrumb subbar

    const setOrgFilter = (key: keyof FilterInput, value: string, selectOne?: boolean) => {
        const isActive = orgFilters[key][value];
        setOrgFilters({ ...orgFilters, [key]: { ...(selectOne ? {} : orgFilters[key]), [value]: !isActive } });
    };

    const setSystemFilter = (key: keyof SystemFilter, value: string, selectOne?: boolean) => {
        const isActive = systemFilters[key][value];
        setSystemFilters({ ...systemFilters, [key]: { ...(selectOne ? {} : systemFilters[key]), [value]: !isActive } });
    };

    const filterParams = useMemo(() => {
        const { companies, resellers, locations } = orgFilters;
        const companyIds = Object.keys(companies)
            .filter((key) => companies[key])
            .join(',');
        const resellerIds = Object.keys(resellers)
            .filter((key) => resellers[key])
            .join(',');
        const locationIds = Object.keys(locations)
            .filter((key) => locations[key])
            .join(',');
        return { companyIds, resellerIds, locationIds };
    }, [orgFilters]);

    const handleOrgClear = () => {
        setOrgFilters({
            companies: {},
            resellers: {},
            locations: {},
        });
    };

    const handleSystemClear = () => {
        setSystemFilters({
            model: {},
            status: {},
        });
        setSearchTerm('');
        setAssigned(SystemAssigned.Assigned);
    };

    const handleFilterChange = (newFilters: FilterInput, newTab: Selection) => {
        setOrgFilters(newFilters);
        setSelectedTab(newTab);
    };

    return (
        <>
            <BreadcrumbSubBar />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flex: 1,
                    mt: '160px',
                    minHeight: 0,
                    position: 'relative', // allow absolutely-positioned children to scroll with content
                }}>
                {/* Toggle Filters edge button */}
                <Tooltip title={filtersOpen ? 'Hide filters' : 'Show filters'}>
                    <IconButton
                        onClick={() => setFiltersOpen((prev) => !prev)}
                        sx={(theme) => ({
                            position: 'absolute', // scrolls with OrgNav content
                            top: 8, // align with drawer header inside the scrollable area
                            left: filtersOpen ? FILTER_DRAWER_WIDTH_PX - TOGGLE_BTN_WIDTH / 2 : -20,
                            width: TOGGLE_BTN_WIDTH,
                            height: TOGGLE_BTN_HEIGHT,
                            borderRadius: '4px',
                            // border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: 'background.paper',
                            zIndex: 10,
                            '&:hover': {
                                backgroundColor: 'background.default',
                            },
                        })}>
                        {filtersOpen ? (
                            <>
                                <ChevronLeftIcon fontSize='small' />
                            </>
                        ) : (
                            <>
                                <ChevronRightIcon fontSize='small' />
                            </>
                        )}
                    </IconButton>
                </Tooltip>

                {/* Filters Panel */}
                <FiltersDrawer
                    open={filtersOpen}
                    selectedTab={selectedTab}
                    setSelectedTab={(nextTab) => {
                        setSelection({ type: null, id: null });
                        setSelectedTab(nextTab);
                    }}
                    orgFilters={orgFilters}
                    setOrgFilter={setOrgFilter}
                    systemFilters={systemFilters}
                    setSystemFilter={setSystemFilter}
                    assigned={assigned}
                    setAssigned={setAssigned}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    onClearOrg={handleOrgClear}
                    onClearSystem={handleSystemClear}
                    onCreate={(type) => {
                        setSelection({ type, id: null });
                    }}
                />

                {/* Main Content */}
                <Box display='flex' flexDirection='column' flex={1} sx={{ minWidth: 0, ml: '20px' }}>
                    {/* Data / Tables area */}
                    {!selection.type && (
                        <Paper
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                overflowY: 'auto',
                                maxHeight: 'calc(100vh - 180px)',
                                pb: selection.type ? 0 : 1,
                                pr: selection.type ? 0 : 1,
                                pl: selection.type ? 0 : 1,
                                overflowX: 'hidden',
                                minHeight: 0,
                            }}>
                            <Box
                                display={'flex'}
                                flexDirection={'row'}
                                height={'100%'}
                                width={'100%'}
                                flex={1}
                                sx={{
                                    overflow: 'auto',
                                    minWidth: 0,
                                }}>
                                {[Selection.Company, Selection.Reseller, Selection.Location].includes(selectedTab) && (
                                    <OrgTable
                                        tab={selectedTab as Selection.Company | Selection.Reseller | Selection.Location}
                                        orgFilters={filterParams}
                                        onFilterChange={handleFilterChange}
                                    />
                                )}

                                {selectedTab === Selection.Employee && (
                                    <EmployeesTable
                                        orgFilters={filterParams}
                                        searchText={searchTerm}
                                        onSelect={(e) => setSelection({ type: Selection.Employee, id: e.EmployeeID! })}
                                        onDelete={(e) => setDeleteEmployee(e)}
                                    />
                                )}

                                {selectedTab === Selection.System && (
                                    <SystemTable
                                        assigned={assigned}
                                        filters={systemFilters}
                                        orgFilters={filterParams}
                                        searchTerm={searchTerm}
                                    />
                                )}
                            </Box>
                        </Paper>
                    )}

                    {selection.type && (
                        <Box
                            display={'flex'}
                            flexDirection={'column'}
                            width={'100%'}
                            flex={1}
                            sx={{
                                overflow: 'auto',
                                minHeight: 0,
                            }}>
                            <Panel
                                selection={selection}
                                onSuccess={() => {
                                    setSelection({
                                        type: null,
                                        id: null,
                                    });
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        </>
    );
};
